/**
 * HEROFORCE — Theme JavaScript
 * Production-ready interactions
 */

"use strict";

/* ============================================================
   UTILITIES
   ============================================================ */

const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

function formatMoney(cents) {
  const currency = window.Shopify?.currency?.active || "USD";
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(
    cents / 100,
  );
}

if (!customElements.get("hf-trust-bar")) {
  class HfTrustBar extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.message = this.querySelector("[data-trust-message]");
      this.progress = this.querySelector("[data-trust-progress]");
      this.status = this.querySelector("[data-trust-status]");
      this.slides = this.querySelector("[data-trust-slides]");
      this.dots = [...this.querySelectorAll("[data-trust-dot]")];
      this.line = this.querySelector('[role="progressbar"]');
      if (
        !this.message ||
        !this.progress ||
        !this.status ||
        !this.slides ||
        !this.line
      )
        return;

      this.initialized = true;
      this.slideIndex = 0;
      this.isPaused = false;
      this.dots.forEach((dot) => {
        dot.addEventListener("click", () =>
          this.showSlide(Number(dot.dataset.trustDot)),
        );
      });
      this.status.addEventListener("mouseenter", () => (this.isPaused = true));
      this.status.addEventListener("mouseleave", () => (this.isPaused = false));
      this.status.addEventListener("focusin", () => (this.isPaused = true));
      this.status.addEventListener("focusout", () => (this.isPaused = false));
      this.carouselTimer = window.setInterval(() => {
        if (!this.isPaused) this.showSlide(this.slideIndex + 1);
      }, 5000);
      this.cartUpdateUnsubscriber =
        typeof subscribe === "function"
          ? subscribe("cart-update", () => this.refresh())
          : null;
      this.refresh();
    }

    disconnectedCallback() {
      this.cartUpdateUnsubscriber?.();
      this.cartUpdateUnsubscriber = null;
      window.clearInterval(this.carouselTimer);
      this.initialized = false;
    }

    showSlide(index) {
      this.slideIndex = (index + this.dots.length) % this.dots.length;
      this.slides.style.transform = `translateX(-${this.slideIndex * 100}%)`;
      this.dots.forEach((dot, dotIndex) => {
        dot.setAttribute("aria-selected", String(dotIndex === this.slideIndex));
      });
    }

    async refresh() {
      try {
        const response = await fetch("/cart.js", {
          headers: { Accept: "application/json" },
        });
        if (!response.ok) throw new Error("Cart unavailable");
        const cart = await response.json();
        const hasItems = cart.item_count > 0;
        const progress = hasItems ? 100 : 0;

        this.message.textContent = hasItems
          ? "YOU ARE ELIGIBLE FOR FREE SHIPPING."
          : "ADD AN ITEM TO UNLOCK TRACKED SHIPPING.";
        this.progress.style.width = `${progress}%`;
        this.line.setAttribute("aria-valuenow", progress);
        this.classList.toggle("is-ready", hasItems);
      } catch (error) {
        this.message.textContent = "TRACKED SHIPPING ON EVERY ORDER.";
      }
    }
  }

  customElements.define("hf-trust-bar", HfTrustBar);
}

/* ============================================================
   HEADER — Scroll + hamburger
   ============================================================ */

function initHeader() {
  const header = $("#site-header");
  if (!header) return;

  let lastY = 0;

  const onScroll = () => {
    const y = window.scrollY;
    header.classList.toggle("is-scrolled", y > 50);
    lastY = y;
  };

  window.addEventListener("scroll", onScroll, { passive: true });
  onScroll(); // run on load
}

/* ============================================================
   MOBILE NAV
   ============================================================ */

function initMobileNav() {
  const hamburger = $("#header-hamburger");
  const nav = $("#mobile-nav");
  if (!hamburger || !nav) return;

  let open = false;
  const spans = $$("span", hamburger);

  const toggle = () => {
    open = !open;
    nav.classList.toggle("is-open", open);
    document.body.style.overflow = open ? "hidden" : "";

    if (open) {
      spans[0].style.transform = "translateY(6px) rotate(45deg)";
      spans[1].style.opacity = "0";
      spans[2].style.transform = "translateY(-6px) rotate(-45deg)";
    } else {
      spans[0].style.transform = "";
      spans[1].style.opacity = "";
      spans[2].style.transform = "";
    }
  };

  hamburger.addEventListener("click", toggle);

  $$("a, button", nav).forEach((el) => {
    el.addEventListener("click", () => {
      if (open) toggle();
    });
  });

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && open) toggle();
  });
}

/* ============================================================
   SCROLL REVEAL — IntersectionObserver
   ============================================================ */

function initScrollReveal() {
  const els = $$(".reveal");
  if (!els.length) return;

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-visible");
          io.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.08, rootMargin: "0px 0px -40px 0px" },
  );

  els.forEach((el) => io.observe(el));
}

/* ============================================================
   MARQUEE — Duplicate items for seamless loop
   ============================================================ */

function initMarquee() {
  const track = $(".marquee-track");
  if (!track) return;

  const items = $$(".marquee-item", track);
  items.forEach((item) => {
    const clone = item.cloneNode(true);
    clone.setAttribute("aria-hidden", "true");
    track.appendChild(clone);
  });
}

/* ============================================================
   BESTSELLERS — Drag-scroll + progress bar
   ============================================================ */

function initBestsellers() {
  const wrap = $(".bestsellers__scroll-wrap");
  const bar = $(".bestsellers__bar");
  if (!wrap) return;

  // Progress bar
  if (bar) {
    let ticking = false;
    const updateBar = () => {
      const max = wrap.scrollWidth - wrap.clientWidth;
      if (!max) return;
      const pct = (wrap.scrollLeft / max) * 100;
      bar.style.width = Math.min(pct + 8, 100) + "%";
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(updateBar);
        ticking = true;
      }
    };
    wrap.addEventListener("scroll", onScroll, { passive: true });
    requestAnimationFrame(updateBar);
  }

  // Drag to scroll
  let isDragging = false,
    startX = 0,
    scrollLeft = 0;

  wrap.addEventListener("mousedown", (e) => {
    isDragging = true;
    startX = e.pageX - wrap.offsetLeft;
    scrollLeft = wrap.scrollLeft;
    wrap.style.userSelect = "none";
  });

  document.addEventListener("mouseup", () => {
    isDragging = false;
    wrap.style.userSelect = "";
  });

  document.addEventListener("mousemove", (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - wrap.offsetLeft;
    const walk = (x - startX) * 1.2;
    wrap.scrollLeft = scrollLeft - walk;
  });
}

/* ============================================================
   CART DRAWER — Full AJAX cart
   ============================================================ */

const Cart = {
  overlay: null,
  drawer: null,
  body: null,
  badge: null,
  subtotalEl: null,

  init() {
    this.overlay = $("#cart-overlay");
    this.drawer = $("#cart-drawer");
    this.body = $("#cart-drawer-body");
    this.badge = $("#cart-count");
    this.subtotalEl = $("#cart-subtotal-price");

    if (!this.drawer) return;

    // Open buttons
    $$("[data-cart-open]").forEach((btn) =>
      btn.addEventListener("click", () => this.open()),
    );
    // Close buttons
    $$("[data-cart-close]").forEach((btn) =>
      btn.addEventListener("click", () => this.close()),
    );
    // Overlay click
    this.overlay?.addEventListener("click", () => this.close());
    // Escape
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") this.close();
    });

    // Checkout button
    const checkoutBtn = $("#cart-checkout-btn");
    checkoutBtn?.addEventListener("click", () => {
      window.location.href = "/checkout";
    });

    // Continue shopping
    const continueBtn = $("#cart-continue-btn");
    continueBtn?.addEventListener("click", () => this.close());
  },

  open() {
    this.drawer?.classList.add("is-open");
    this.overlay?.classList.add("is-open");
    document.body.style.overflow = "hidden";
    this.fetchCart();
  },

  close() {
    this.drawer?.classList.remove("is-open");
    this.overlay?.classList.remove("is-open");
    document.body.style.overflow = "";
  },

  async fetchCart() {
    try {
      const res = await fetch("/cart.js", {
        headers: { "Content-Type": "application/json" },
      });
      const cart = await res.json();
      this.render(cart);
    } catch (err) {
      console.warn("[HeroForce] Cart fetch error:", err);
    }
  },

  async addItem(variantId, quantity = 1, properties = {}) {
    try {
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: variantId, quantity, properties }),
      });
      const data = await res.json();

      if (data.status === 422 || data.status === 404) {
        throw new Error(data.description || "Could not add item.");
      }

      await this.fetchCart();
      this.open();
    } catch (err) {
      console.error("[HeroForce] Add to Cart error:", err);
      this.showError(err.message);
    }
  },

  async updateItem(key, quantity) {
    try {
      await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: key, quantity }),
      });
      await this.fetchCart();
    } catch (err) {
      console.error("[HeroForce] Cart update error:", err);
    }
  },

  render(cart) {
    // Update badge
    if (this.badge) {
      this.badge.textContent = cart.item_count;
      this.badge.style.display = cart.item_count > 0 ? "inline-flex" : "none";
    }

    if (!this.body) return;

    // Update count label
    const countLabel = $("#cart-item-count");
    if (countLabel) {
      countLabel.textContent = `${cart.item_count} ${cart.item_count === 1 ? "item" : "items"}`;
    }

    if (cart.item_count === 0) {
      this.body.innerHTML = `
        <div class="cart-empty">
          <svg width="56" height="56" fill="none" stroke="currentColor" stroke-width="1" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/>
          </svg>
          <p>Your selection is empty.</p>
          <button class="btn btn-ghost" style="margin-top:1.5rem" onclick="Cart.close()">Explore Collection</button>
        </div>`;

      if (this.subtotalEl) this.subtotalEl.textContent = formatMoney(0);
      return;
    }

    this.body.innerHTML = cart.items
      .map(
        (item) => `
      <div class="cart-item" data-key="${item.key}">
        <div class="cart-item__img">
          ${
            item.image
              ? `<img src="${item.image}" alt="${item.product_title}" loading="lazy" width="80" height="80">`
              : '<div style="width:100%;height:100%;background:var(--clr-card)"></div>'
          }
        </div>
        <div>
          <div class="cart-item__name">${item.product_title}</div>
          ${
            item.variant_title && item.variant_title !== "Default Title"
              ? `<div class="cart-item__variant">${item.variant_title}</div>`
              : ""
          }
          <div class="cart-item__row" style="margin-top:.5rem">
            <div class="qty-ctrl">
              <button class="js-qty-minus" data-key="${item.key}" data-qty="${item.quantity - 1}" aria-label="Decrease">−</button>
              <input type="number" value="${item.quantity}" min="0" readonly aria-label="Quantity">
              <button class="js-qty-plus" data-key="${item.key}" data-qty="${item.quantity + 1}" aria-label="Increase">+</button>
            </div>
            <div class="cart-item__price">${formatMoney(item.final_line_price)}</div>
          </div>
          <button class="cart-item__remove js-remove" data-key="${item.key}" data-qty="0">Remove</button>
        </div>
      </div>`,
      )
      .join("");

    if (this.subtotalEl)
      this.subtotalEl.textContent = formatMoney(cart.total_price);

    // Bind qty + remove controls
    $$(".js-qty-minus, .js-qty-plus, .js-remove", this.body).forEach((btn) => {
      btn.addEventListener("click", () => {
        const key = btn.dataset.key;
        const qty = parseInt(btn.dataset.qty, 10);
        this.updateItem(key, qty);
      });
    });
  },

  showError(msg) {
    const toast = document.createElement("div");
    toast.className = "cart-toast";
    toast.textContent = msg || "An error occurred.";
    toast.style.cssText = `
      position:fixed;bottom:2rem;left:50%;transform:translateX(-50%);
      background:#C0392B;color:#fff;padding:.8rem 1.5rem;
      font-size:.78rem;letter-spacing:.08em;z-index:999;
      border-radius:2px;box-shadow:0 4px 20px rgba(0,0,0,.4);`;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 3500);
  },
};

// expose globally for inline onclick handlers
window.Cart = Cart;

/* ============================================================
   PRODUCT GALLERY
   ============================================================ */

function initProductGallery() {
  const mainImg = $("#product-main-image");
  const thumbs = $$(".gallery__thumb");
  if (!mainImg || !thumbs.length) return;

  thumbs.forEach((thumb) => {
    thumb.addEventListener("click", () => {
      const src = thumb.dataset.src;
      const srcset = thumb.dataset.srcset || "";

      mainImg.style.opacity = "0";
      mainImg.style.transform = "scale(0.97)";

      setTimeout(() => {
        mainImg.src = src;
        if (srcset) mainImg.srcset = srcset;
        mainImg.style.opacity = "1";
        mainImg.style.transform = "scale(1)";
      }, 220);

      thumbs.forEach((t) => t.classList.remove("is-active"));
      thumb.classList.add("is-active");
    });
  });

  mainImg.style.transition = "opacity .22s ease, transform .3s ease";
}

/* ============================================================
   VARIANT SELECTOR
   ============================================================ */

function initVariantSelector() {
  const form = $("#product-form");
  const addBtn = $("#add-to-cart-btn");
  const priceEl = $("#product-price");
  const variantInput = $("#variant-id");

  if (!form || !addBtn) return;

  // Variant buttons
  $$(".js-variant-btn").forEach((btn) => {
    btn.addEventListener("click", () => {
      if (btn.classList.contains("is-sold-out")) return;

      const group = btn.closest("[data-option-group]");
      $$(".js-variant-btn", group).forEach((b) =>
        b.classList.remove("is-active"),
      );
      btn.classList.add("is-active");

      // Update hidden variant input & price
      const variantId = btn.dataset.variantId;
      const price = btn.dataset.price;

      if (variantId && variantInput) variantInput.value = variantId;
      if (price && priceEl)
        priceEl.textContent = formatMoney(parseInt(price, 10));
    });
  });

  // Add to cart
  addBtn.addEventListener("click", async () => {
    const variantId = variantInput?.value;
    if (!variantId) return;

    const qtyInput = $("#quantity-input");
    const quantity = qtyInput ? parseInt(qtyInput.value, 10) : 1;

    addBtn.disabled = true;
    const original = addBtn.innerHTML;
    addBtn.innerHTML = `<svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" style="animation:spin .8s linear infinite"><path d="M4 4v5h5M20 20v-5h-5"/><path d="M4.07 15a8 8 0 1012.73-9.36L20 20 4.07 15z"/></svg> Adding...`;

    await Cart.addItem(variantId, quantity);

    addBtn.disabled = false;
    addBtn.innerHTML = original;
  });
}

/* ============================================================
   QUANTITY CONTROLS (product page standalone)
   ============================================================ */

function initQtyControls() {
  const input = $("#quantity-input");
  const minusBtn = $("#qty-btn-minus");
  const plusBtn = $("#qty-btn-plus");

  if (!input) return;

  minusBtn?.addEventListener("click", () => {
    const v = parseInt(input.value, 10);
    if (v > 1) input.value = v - 1;
  });
  plusBtn?.addEventListener("click", () => {
    input.value = parseInt(input.value, 10) + 1;
  });
}

/* ============================================================
   QUICK ADD (collection / featured cards)
   ============================================================ */

function initQuickAdd() {
  $$(".js-quick-add").forEach((btn) => {
    btn.addEventListener("click", async (e) => {
      e.preventDefault();
      const variantId = btn.dataset.variantId;
      if (!variantId) return;

      btn.textContent = "...";
      await Cart.addItem(variantId, 1);
      btn.textContent = btn.dataset.label || "Add to Cart";
    });
  });
}

/* ============================================================
   NEWSLETTER FORM
   ============================================================ */

function initNewsletter() {
  const form = $("#newsletter-form");
  if (!form) return;

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const email = form.querySelector('input[type="email"]')?.value;
    const btn = form.querySelector(".newsletter__submit");
    if (!email || !btn) return;

    const originalText = btn.textContent;
    btn.textContent = "...";
    btn.disabled = true;

    try {
      // Shopify accepts contact form submissions via /contact
      const body = new URLSearchParams({
        form_type: "customer",
        utf8: "✓",
        "contact[email]": email,
        "contact[tags]": "newsletter",
      });

      await fetch("/contact#newsletter-form", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      btn.textContent = "✓ Subscribed";
      btn.style.background = "#2ECC71";
      form.reset();

      setTimeout(() => {
        btn.textContent = originalText;
        btn.disabled = false;
        btn.style.background = "";
      }, 3500);
    } catch (err) {
      btn.textContent = originalText;
      btn.disabled = false;
    }
  });
}

/* ============================================================
   COLLECTION FILTERS (client-side)
   ============================================================ */

function initCollectionFilters() {
  const filterBtns = $$(".js-filter-tag");
  if (!filterBtns.length) return;

  filterBtns.forEach((btn) => {
    btn.addEventListener("click", () => {
      filterBtns.forEach((b) => b.classList.remove("is-active"));
      btn.classList.add("is-active");
      // Full page filter via URL would be handled by Shopify
      const tag = btn.dataset.tag;
      if (tag && tag !== "all") {
        window.location.href = `/collections/${window.HF?.collectionHandle}/${tag}`;
      } else {
        window.location.href = `/collections/${window.HF?.collectionHandle}`;
      }
    });
  });
}

/* ============================================================
   SMOOTH ANCHOR LINKS
   ============================================================ */

function initSmoothLinks() {
  $$('a[href^="#"]').forEach((link) => {
    link.addEventListener("click", (e) => {
      const target = document.getElementById(
        link.getAttribute("href").slice(1),
      );
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  });
}

/* ============================================================
   INIT ALL
   ============================================================ */

document.addEventListener("DOMContentLoaded", () => {
  initHeader();
  initMobileNav();
  initScrollReveal();
  initMarquee();
  initBestsellers();
  Cart.init();
  initProductGallery();
  initVariantSelector();
  initQtyControls();
  initQuickAdd();
  initNewsletter();
  initCollectionFilters();
  initSmoothLinks();
});
